import * as React from "react";
import { Modal, Menu, Dropdown, Switch } from "antd";
import type { MenuProps } from "antd";
import { useNavigate } from "react-router-dom";
import { TimezoneDisplay } from "../TimezoneDisplay";
import { AlertCircle, LogOut, Tool } from "react-feather";
import { UserAvatar } from "./UserAvatar";
import { DOCS_URL, OPENAPI_URL, STATUS_URL } from "../../../../envVariables";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { respectOSTheme, setTheme } from "../../../../store/themeSlice";
import { selectHasOrgInvite, selectOrgInvites } from "../../../../store/userDataSlice";
import { handleSignOut, selectIsAdmin, selectIsCplnTeam } from "../../../../store/userSlice";
import { sentryLog } from "../../../../tools/sentry";
import { getTargetHost } from "../ConsoleSwitch";
import { resetGuide, resetAllGuides, getGuideKeys, GUIDE_NAMES, GuideKey } from "../../../../guides/guides";

export const ProfileMenu: React.FC = () => {
  const userName = useAppSelector((state) => state.user.name);
  const userEmail = useAppSelector((state) => state.user.email);
  const isCplnTeam = useAppSelector(selectIsCplnTeam);
  const isAdmin = useAppSelector(selectIsAdmin);

  const dashboard = useAppSelector((state) => state.uiData.dashboard);

  const orgInvites = useAppSelector(selectOrgInvites);
  const hasOrgInvite = useAppSelector(selectHasOrgInvite);

  const dispatch = useAppDispatch();
  const themeSlice = useAppSelector((state) => state.theme);

  const navigate = useNavigate();

  const [isLogoutOpen, setIsLogoutOpen] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  function onOpenChange(flag: boolean) {
    setOpen(flag);
  }

  function handleMenuClick(e: Parameters<NonNullable<MenuProps["onClick"]>>[0]) {
    let hide = true;
    switch (true) {
      case e.key === "1":
        navigate("/home/profile");
        break;
      case e.key === "3":
      case e.key === "4":
        e.domEvent.preventDefault();
        e.domEvent.stopPropagation();
        hide = false;
        break;
      case e.key === "billing-accounts":
        navigate("/home/account");
        break;
      case e.key === "tasks":
        if (!hasOrgInvite) {
          return;
        }
        navigate("/task");
        break;
      case e.key === "status":
        window.open(STATUS_URL, "_blank");
        break;
      case e.key === "openapi":
        window.open(OPENAPI_URL, "_blank");
        break;
      case e.key === "console-switch":
        window.location.href = getTargetHost() + `?preferredmode=legacy`;
        break;
      case e.key === "reset-all-guides":
        resetAllGuides();
        break;
      case e.key.startsWith("reset-guide-"):
        const guideKey = e.key.replace("reset-guide-", "") as GuideKey;
        resetGuide(guideKey);
        break;
      case e.key === "useostheme":
        dispatch(respectOSTheme());
        break;
      case e.key === "documentation":
        window.open(DOCS_URL, "_blank");
        break;
      case e.key === "logout":
        setIsLogoutOpen(!isLogoutOpen);
        break;
      default:
        break;
    }
    setOpen(!hide);
  }

  const devItemStyle = { display: "flex", alignItems: "center", gap: 8 };

  const menuItems: MenuProps["items"] = React.useMemo(() => {
    const items: MenuProps["items"] = [
      {
        key: "1",
        label: (
          <div style={{ display: "flex", alignItems: "center" }}>
            <UserAvatar size={24} />
            <div className="flex flex-col" style={{ marginLeft: 8 }}>
              <span>{userName}</span>
              <span>{userEmail}</span>
            </div>
          </div>
        ),
      },
      { type: "divider" },
      {
        key: "billing-accounts",
        label: "Billing Accounts",
      },
      { type: "divider" },
    ];

    if (dashboard !== "task") {
      items.push({
        key: "tasks",
        label: (
          <div className="flex items-center gap-2">
            {hasOrgInvite ? `${orgInvites.length} Task${orgInvites.length > 1 ? "s" : ""}` : "No Tasks"}
            {hasOrgInvite ? (
              <AlertCircle
                style={{ color: `var(--color-danger) !important`, transform: "translateY(1px)" }}
                size={16}
              />
            ) : null}
          </div>
        ),
      });
    }

    items.push({
      key: "3",
      label: <TimezoneDisplay />,
    });

    if (!themeSlice.isRespectingOS) {
      items.push({
        key: "useostheme",
        label: <span>Use OS Theme Preference</span>,
      });
    }

    items.push({
      key: "4",
      label: (
        <div className="w-full h-full block flex items-center gap-2">
          <label htmlFor="theme-switch">Dark Theme</label>
          <Switch
            id={`theme-switch`}
            checked={themeSlice.theme === "dark"}
            onChange={(value) => dispatch(setTheme(value ? "dark" : "light"))}
            size={`small`}
          />
        </div>
      ),
    });

    items.push(
      {
        key: "status",
        label: <span>System Status</span>,
      },
      {
        key: "openapi",
        label: <span>Open API</span>,
      },
      {
        key: "documentation",
        label: <span>Documentation</span>,
      },
      {
        key: "console-switch",
        label: <span>Use Legacy UI</span>,
      },
      { type: "divider" },
    );

    if (!isAdmin) {
      items.push({
        key: "logout",
        label: (
          <span className="user-menu-log-out">
            Log out
            <LogOut size={20} color="var(--color-default)" />
          </span>
        ),
      });
    }

    if (isCplnTeam) {
      items.push(
        { type: "divider" },
        {
          key: "guides-submenu",
          label: (
            <span style={devItemStyle}>
              <Tool size={14} />
              Reset Guides
            </span>
          ),
          children: [
            {
              key: "reset-all-guides",
              label: <span>Reset All Guides</span>,
            },
            { type: "divider" },
            ...getGuideKeys().map((key) => ({
              key: `reset-guide-${key}`,
              label: <span>{GUIDE_NAMES[key]}</span>,
            })),
          ],
        },
      );
    }

    return items;
  }, [userName, userEmail, dashboard, hasOrgInvite, orgInvites.length, themeSlice.isRespectingOS, themeSlice.theme, isAdmin, isCplnTeam, dispatch]);

  const menu = () => (
    <Menu items={menuItems} selectable={false} onClick={handleMenuClick} className="user-menu" />
  );

  return (
    <>
      <div className="flex items-center h-full">
        <Dropdown
          popupRender={menu}
          className="flex items-center"
          trigger={["click"]}
          open={open}
          onOpenChange={onOpenChange}
        >
          <div data-guide="user-menu">
            <UserAvatar showAdditions size={32} />
          </div>
        </Dropdown>
      </div>
      <Modal
        title="Confirm Logout"
        maskClosable={false}
        open={isLogoutOpen}
        onCancel={() => setIsLogoutOpen(!isLogoutOpen)}
        onOk={async () => {
          sentryLog("intentional sign out");
          await handleSignOut(false);
        }}
      >
        <span style={{ color: "var(--color-label-secondary)" }}>Are you sure you want to log out of your account?</span>
      </Modal>
    </>
  );
};
